/**
 * 从当前浏览器地址反推「正在用哪个工作流」。
 *
 * 用途：用户在上传区传参考图时，后端需要知道**这张图是在哪传的**才能归类。
 * 在「模特生成」页传的图和在「白底主图」页传的图，同为上传素材但用途完全不同。
 *
 * 为什么从 URL 推而不是让每个页面主动上报：
 * 页面主动上报要求 7 个页面各自记得调用一次，漏一个就永久归类错误；
 * URL 是路由系统的唯一真源，页面再怎么改，地址栏骗不了人。
 *
 * 规则必须与 `src/pages/FashionStudio.tsx` 里 appSlug 的算法保持一致，
 * 否则上传的参考图与它参与生成的成品会掉进不同类别。
 *
 * 合并页（`/detail-page` 的出图模式、`/video-studio` 的视频档位）是例外：
 * 一条路由承载多个模式，所以规则是「路由 + `?mode=`」，映射表分别在
 * `config/studioModes.ts` 与 `config/videoModes.ts`。
 */
import { getMode } from '@/config/studioModes'
import { getStudioVideoMode, getVideoMode } from '@/config/videoModes'

/** 路径 + query → app_slug（与提交任务时用的是同一个标识） */
export function appSlugFromLocation(
  pathname: string = window.location.pathname,
  search: string = window.location.search,
): string {
  const seg = pathname.split('/').filter(Boolean)
  const params = new URLSearchParams(search)

  switch (seg[0]) {
    case 'professional': {
      const cat = seg[1] ?? 'fashion'
      const feature = params.get('feature') ?? ''
      if (!feature) return `professional/${cat}`
      return `${cat === 'products' ? 'product' : 'fashion'}-${feature}`
    }
    case 'detail-page':
      // **合并页**（主图 / 套图 / 详情 / 平台详情模块 / 模仿版式 / 成片派生）：
      // 七个模式共用一条路由，所以 app_slug 只能由 `?mode=` 决定 ——
      // 而它必须**逐个映射回合并前那个 app_slug**（`ai-set` / `detail-*` /
      // `localize-*`），因为同一串值同时是资产分类键（前端
      // `config/assetTaxonomy.ts` 与后端 `app/taxonomy.py` 的 `_BY_SLUG`）、
      // 后端 `app_kind` 查表键与报价键。映射表在 `config/studioModes.ts` 一份，
      // 这里与页面都从它取 —— 各写一份，改名时必然漏一处。
      return getMode(params.get('mode')).appSlug
    case 'batch':
      /*
       * **批量出图工作台**（2026-09-29 新开）。它在这一层要回答的是同一个问题：
       * 「用户在 `/batch` 拖进来的那批参考图，该记成哪一档的素材」——
       * 那是 `classifyLocal()` 的第一顺位入参，直接决定素材库里的归类。
       *
       * ⚠️ 与上一支的差别只有一个，而且**是刻意的**：`/detail-page` 的 `?mode=`
       * 是**一次性入站指令**（老书签 `/ai-set` 进来铺一次形态就摘掉），
       * 而 `/batch` 没有「入站指令」这回事（它不是任何老地址的落点），
       * 那一页把当前形态**持续同步**在地址栏里（见 BatchStudio 里那个 effect）。
       * 两边读的是同一个键名，因为它就是同一个概念（产出形态）；
       * 生命周期不同是两页的设计使然，不是两套语义。
       *
       * 不认这一段的话，`/batch` 上传的图会掉进 `classifyLocal` 的兜底 `other` ——
       * 界面上完全看不出来，但素材库会脏，且不可逆。
       */
      return getMode(params.get('mode')).appSlug
    case 'img-localize':
      return `tool-${params.get('tool') ?? 'remove-bg'}`
    case 'video-studio':
      // **合并页**。2026-09-29 先把「带货短视频 / 商品视频」两条路由合成一条，
      // 2026-10-05 连档位也并成一档，所以现在**恒返回 `short-video`**。
      // 这串值同时是资产分类键（`config/assetTaxonomy.ts` 与后端 `app/taxonomy.py`）、
      // 后端 `app_kind` 查表键（`app/registry.py`）与报价键，映射表在
      // `config/videoModes.ts` 一份，页面与这里都从它取。
      //
      // ⚠️ 认不出的 `?mode=`（比如**已下架**的 `main-video`、或用户手改的乱值）
      // 由 `getStudioVideoMode()` 统一回落到主力档 —— 所以旧地址上传的素材
      // 会记成 `short-video`，这正是我们想要的（新素材不该再写进一个已下线的档）。
      // 库里那批 `app_slug = 'main-video'` 的**旧任务**不受影响：它们已经写完盘了。
      return getStudioVideoMode(params.get('mode')).appSlug
    case 'video-replicate':
      // 视频复刻**没有合并**：模式由路径唯一决定（用户原话「视频复刻不变」）
      return getVideoMode('video-replicate').appSlug
    case 'banner':
      return seg[0]
    case 'copywriting':
      return 'copywriting'
    default:
      return ''
  }
}

/** 页面路径（不是 app_slug），用于 source_page 兜底 */
export function sourcePageFromLocation(
  pathname: string = window.location.pathname,
): string {
  const seg = pathname.split('/').filter(Boolean)
  if (seg[0] === 'professional') return `professional/${seg[1] ?? 'fashion'}`
  return seg[0] ?? ''
}
